/**
 * Monthly memberships are subscription-first and webhook-activated: `POST payment/card-payment`
 * answers `membership_status: 'pending'` / `awaiting_webhook: true`, and the membership only
 * becomes active when the Stripe webhook lands (realtime `payment_done`). One-time payments
 * (single_wash / add_on / prepaid) still resolve synchronously and never hit these helpers.
 */

export const MEMBERSHIP_CARD_NOT_REUSABLE_CODE = 'card_not_reusable_for_subscription';
export const MEMBERSHIP_SUBSCRIPTION_SETUP_FAILED_CODE = 'membership_subscription_setup_failed';

/** Shown while the Stripe webhook is still in flight. */
export const MEMBERSHIP_ACTIVATION_PENDING_MESSAGE = 'Confirming your membership…';

/**
 * How long to wait for the realtime `payment_done` / `payment_failed` event before falling back to
 * polling the order.
 *
 * This is a fallback for a lost event, so it must be short: it used to be 45s, which meant a
 * dropped SSE event stranded the attendant at the counter for 45 seconds before the first poll
 * even ran. Five seconds is comfortably longer than a healthy webhook + SSE round-trip, so the
 * normal path still completes on the event and never polls at all.
 */
export const MEMBERSHIP_ACTIVATION_WAIT_MS = 5000;

/**
 * Poll `GET order/:id` this many times, this far apart, once the wait window expires.
 *
 * The attempt budget carries the total observation window, so shortening the wait above must not
 * shorten it: a genuinely slow Stripe webhook has been seen arriving around 40s and must still be
 * caught rather than reported as delayed.
 *
 * The last request goes out at `WAIT + (ATTEMPTS - 1) * INTERVAL` — the first poll fires when the
 * wait expires, and only the gaps between polls cost an interval — so 12 attempts observe until
 * 60s. (The previous 45s/3-attempt pairing stopped at 55s.)
 */
export const MEMBERSHIP_ACTIVATION_POLL_ATTEMPTS = 12;
export const MEMBERSHIP_ACTIVATION_POLL_INTERVAL_MS = 5000;

/**
 * Neutral copy for a webhook that has not arrived within the wait window. The charge was
 * accepted, so this is never presented as a hard failure.
 */
export const MEMBERSHIP_ACTIVATION_DELAYED_MESSAGE =
  'Still confirming this membership with Stripe. The payment went through — check this order again in a moment.';

const MEMBERSHIP_FAILURE_MESSAGES_BY_CODE: Record<string, string> = {
  [MEMBERSHIP_CARD_NOT_REUSABLE_CODE]: "Your card can't be used to purchase a membership.",
  [MEMBERSHIP_SUBSCRIPTION_SETUP_FAILED_CODE]:
    'We could not set up this membership with Stripe. Any charge was reversed — please try another card.',
};

export const MEMBERSHIP_ACTIVATION_FALLBACK_FAILURE_MESSAGE =
  MEMBERSHIP_FAILURE_MESSAGES_BY_CODE[MEMBERSHIP_CARD_NOT_REUSABLE_CODE];

function asRecord(value: any): Record<string, any> {
  return value && typeof value === 'object' ? (value as Record<string, any>) : {};
}

/** Body of a plain response, of an `HttpErrorResponse`, and its nested `data` envelope. */
function readPayloadLayers(payload: any): {
  root: Record<string, any>;
  body: Record<string, any>;
  data: Record<string, any>;
} {
  const root = asRecord(payload);
  const body = asRecord(root['error']);
  const data = asRecord(root['data'] ?? body['data']);
  return { root, body, data };
}

function firstNonEmptyString(...values: any[]): string {
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (text) {
      return text;
    }
  }
  return '';
}

const ENTITLED_SUBSCRIPTION_STATUSES = new Set(['active', 'trialing']);

/** Real subscription id from a card-payment or create-payment-intent payload. */
export function resolveMembershipSubscriptionId(payload: any): string {
  const { root, data } = readPayloadLayers(payload);
  const id = firstNonEmptyString(
    root['subscription_id'],
    root['stripe_subscription_id'],
    data['subscription_id'],
    data['stripe_subscription_id']
  );
  if (!id || id.toLowerCase() === 'null' || id.toLowerCase() === 'undefined') {
    return '';
  }
  return id;
}

/** Stripe subscription status when the backend surfaces one on the response. */
export function resolveMembershipSubscriptionStatus(payload: any): string {
  const { root, data } = readPayloadLayers(payload);
  return firstNonEmptyString(
    root['subscription_status'],
    data['subscription_status']
  ).toLowerCase();
}

/**
 * Membership is already entitled: a non-empty subscription id plus, when present, an
 * active/trialing status. Missing status with a real id is treated as entitled (same rule as
 * the notification modal).
 */
export function isMembershipEntitledResponse(payload: any): boolean {
  const subscriptionId = resolveMembershipSubscriptionId(payload);
  if (!subscriptionId) {
    return false;
  }
  const status = resolveMembershipSubscriptionStatus(payload);
  if (!status) {
    return true;
  }
  return ENTITLED_SUBSCRIPTION_STATUSES.has(status);
}

/**
 * Safe to treat a membership checkout as accepted: either webhook-pending (`awaiting_webhook`)
 * or already entitled. A bare `{ status: true, success: true }` without either is not accepted.
 */
export function isMembershipCheckoutAccepted(payload: any): boolean {
  return isMembershipActivationPending(payload) || isMembershipEntitledResponse(payload);
}

/**
 * `card-payment` accepted the charge but the membership is not active yet
 * (`membership_status: 'pending'` / `awaiting_webhook: true`).
 */
export function isMembershipActivationPending(payload: any): boolean {
  const { root, data } = readPayloadLayers(payload);
  const membershipStatus = firstNonEmptyString(
    root['membership_status'],
    data['membership_status']
  ).toLowerCase();
  const awaitingWebhook = root['awaiting_webhook'] ?? data['awaiting_webhook'];
  return membershipStatus === 'pending' || awaitingWebhook === true;
}

/**
 * Imported membership card-on-file: Stripe subscription is already created with trial_end
 * (nothing due today). Do not call `card-payment` — there is no invoice PaymentIntent to confirm.
 */
export function isDeferredMembershipCardSetup(payload: any): boolean {
  const { root, data } = readPayloadLayers(payload);
  const alreadyPaid = root['already_paid'] === true || data['already_paid'] === true;
  if (alreadyPaid) {
    return true;
  }
  const clientSecret = firstNonEmptyString(root['client_secret'], data['client_secret']);
  return !clientSecret && isMembershipActivationPending(payload);
}

/** Backend rejected the payment outright (`success: false` / `status: false`). */
export function isRejectedPaymentResponse(payload: any): boolean {
  const root = asRecord(payload);
  return root['status'] === false || root['success'] === false;
}

/** HTTP 402 from `card-payment` or `create-payment-intent` — a clean payment failure. */
export function isPaymentRequiredError(error: any): boolean {
  const { root, body, data } = readPayloadLayers(error);
  return (
    root['status'] === 402 ||
    root['status_code'] === 402 ||
    body['status_code'] === 402 ||
    data['status_code'] === 402
  );
}

/** Structured membership failure code (`code` / `failure_code`) wherever the backend put it. */
export function resolveMembershipFailureCode(payload: any): string {
  const { root, body, data } = readPayloadLayers(payload);
  return firstNonEmptyString(
    root['code'],
    root['failure_code'],
    body['code'],
    body['failure_code'],
    data['code'],
    data['failure_code']
  );
}

/** Backend `message` wins; otherwise map the structured code; otherwise the generic reason. */
export function resolveMembershipFailureMessage(
  payload: any,
  fallback: string = MEMBERSHIP_ACTIVATION_FALLBACK_FAILURE_MESSAGE
): string {
  const { root, body, data } = readPayloadLayers(payload);
  const message = firstNonEmptyString(
    body['message'],
    body['error_message'],
    data['message'],
    root['message'],
    root['error_message']
  );
  if (message) {
    return message;
  }
  return MEMBERSHIP_FAILURE_MESSAGES_BY_CODE[resolveMembershipFailureCode(payload)] || fallback;
}

/** `create-payment-intent` fields the checkout needs for the subscription-first flow. */
export interface MembershipPaymentIntentDetails {
  clientSecret: string;
  requiresAction: boolean;
  membershipStatus: string;
  subscriptionId: string;
}

export function readMembershipPaymentIntentDetails(payload: any): MembershipPaymentIntentDetails {
  const { root, data } = readPayloadLayers(payload);
  const source = Object.keys(data).length ? data : root;
  return {
    clientSecret: firstNonEmptyString(source['client_secret']),
    requiresAction: source['requires_action'] === true,
    membershipStatus: firstNonEmptyString(source['membership_status']).toLowerCase(),
    subscriptionId: firstNonEmptyString(
      source['subscription_id'],
      source['stripe_subscription_id']
    ),
  };
}
